<template>
  <span class="product-tour-host" aria-hidden="true" />
</template>

<script>
import "./product-tours.css";

import { nextTick } from "vue";
import { createTourAdapter, findTourStepIndex } from "@/Utile/tours/tourAdapter.mjs";
import { subscribeToProductTourRequests } from "@/Utile/tours/tourBus.mjs";
import {
  CRM_FULL_TOUR_KEY,
  getTourDefinition,
  localizeTourDefinition,
} from "@/Utile/tours/tourDefinitions.mjs";
import { isTerminalTourProgress, saveTourProgress } from "@/Utile/tours/tourProgress.mjs";
import {
  hasTourAutomaticOptOut,
  loadTourProgressOnce,
  readTourProgress,
  setTourProgressEntry,
  tourProgressState,
  writeTourProgress,
} from "@/Utile/tours/tourProgressStore.mjs";

const AUTO_START_DELAY_MS = 500;
const CRM_ROOT_ROUTE = "/ncrm";

export default {
  name: "ProductTourHost",
  data() {
    return {
      activeTour: null,
      activeAdapter: null,
      lastActiveStep: null,
      autoStartTimer: null,
      suppressAutoStartUntil: 0,
      unsubscribeTourRequests: null,
      progressWriteQueue: Promise.resolve(),
      launcherElement: null,
    };
  },
  watch: {
    "$route.path": {
      handler(nextPath) {
        this.scheduleAutomaticTour(nextPath);
      },
    },
  },
  async mounted() {
    this.unsubscribeTourRequests = subscribeToProductTourRequests((request) => {
      void this.startTour(request.tourKey, request.launchMode, request.returnFocusTo);
    });
    await loadTourProgressOnce();
    this.scheduleAutomaticTour(this.$route?.path);
  },
  beforeUnmount() {
    this.clearAutoStartTimer();
    if (typeof this.unsubscribeTourRequests === "function") this.unsubscribeTourRequests();
    this.unsubscribeTourRequests = null;
    this.activeAdapter?.stop?.();
    this.activeAdapter = null;
  },
  methods: {
    labels() {
      return {
        guide: this.$t("ncrm.tours.controls.guide"),
        next: this.$t("ncrm.tours.controls.next"),
        back: this.$t("ncrm.tours.controls.back"),
        done: this.$t("ncrm.tours.controls.done"),
        skip: this.$t("ncrm.tours.controls.skip"),
        resumeLater: this.$t("ncrm.tours.controls.resumeLater"),
        progress: this.$t("ncrm.tours.controls.progress", {
          current: "{{current}}",
          total: "{{total}}",
        }),
      };
    },
    readProgress(definition) {
      return readTourProgress(definition.key, definition.version);
    },
    clearAutoStartTimer() {
      if (this.autoStartTimer) window.clearTimeout(this.autoStartTimer);
      this.autoStartTimer = null;
    },
    /**
     * Only the cross-route CRM tour starts on its own, once, on the first CRM
     * entry. Section tours are opt-in from the page help button, which carries
     * a discreet dot while the tour has not been seen.
     */
    scheduleAutomaticTour(routePath) {
      this.clearAutoStartTimer();
      if (!tourProgressState.loaded || this.activeTour) return;
      if (Date.now() < this.suppressAutoStartUntil) return;
      if (String(routePath || "") !== CRM_ROOT_ROUTE) return;
      if (hasTourAutomaticOptOut()) return;

      const definition = getTourDefinition(CRM_FULL_TOUR_KEY);
      if (!definition || isTerminalTourProgress(this.readProgress(definition))) return;

      this.autoStartTimer = window.setTimeout(() => {
        this.autoStartTimer = null;
        const progress = this.readProgress(definition);
        const launchMode = progress?.status === "in_progress" ? "resume" : "automatic";
        void this.startTour(CRM_FULL_TOUR_KEY, launchMode);
      }, AUTO_START_DELAY_MS);
    },
    captureLauncherElement(returnFocusTo) {
      if (returnFocusTo) {
        this.launcherElement = returnFocusTo;
        return;
      }
      const active = typeof document !== "undefined" ? document.activeElement : null;
      this.launcherElement = active && active !== document.body ? active : null;
    },
    restoreLauncherFocus() {
      const element = this.launcherElement;
      this.launcherElement = null;
      if (!element || typeof element.focus !== "function") return;
      if (typeof document !== "undefined" && !document.body?.contains(element)) return;
      element.focus({ preventScroll: true });
    },
    queueProgressWrite({ definition, action, step, launchMode }) {
      const payload = {
        tourKey: definition.key,
        tourVersion: definition.version,
        action,
        currentStepKey: step?.key || this.lastActiveStep?.key || null,
        launchMode,
      };
      this.progressWriteQueue = this.progressWriteQueue
        .then(() => saveTourProgress(payload))
        .then((progress) => writeTourProgress(definition.key, definition.version, progress))
        .catch((error) => {
          console.error("[product-tours] progress save failed", error);
          tourProgressState.failed = true;
          return null;
        });
      return this.progressWriteQueue;
    },
    async startTour(tourKey, launchMode = "manual_section", returnFocusTo = null) {
      this.clearAutoStartTimer();
      if (this.activeTour) return;
      const definition = localizeTourDefinition(tourKey, (key) => this.$t(key));
      if (!definition?.steps?.length) return;

      this.captureLauncherElement(returnFocusTo);

      const progress = this.readProgress(definition);
      const startIndex =
        progress?.status === "in_progress"
          ? findTourStepIndex(definition.steps, progress.currentStepKey)
          : 0;
      this.activeTour = { definition, launchMode, returnRoute: this.$route?.fullPath || definition.route };
      this.lastActiveStep = definition.steps[startIndex] || definition.steps[0];
      this.queueProgressWrite({
        definition,
        action: "start",
        step: this.lastActiveStep,
        launchMode,
      });

      const finish = ({ action, step }) => {
        const active = this.activeTour;
        if (!active) return;
        if (["complete", "skip"].includes(action)) {
          const currentProgress = this.readProgress(definition) || {};
          setTourProgressEntry(definition.key, definition.version, {
            ...currentProgress,
            tourKey: definition.key,
            tourVersion: definition.version,
            status: action === "complete" ? "completed" : "skipped",
            currentStepKey: step?.key || this.lastActiveStep?.key || null,
          });
          this.suppressAutoStartUntil = Date.now() + 1500;
        }
        const progressWrite = this.queueProgressWrite({ definition, action, step, launchMode });
        if (["complete", "skip"].includes(action) && this.$route?.fullPath !== active.returnRoute) {
          void progressWrite.then(() => this.$router.push(active.returnRoute));
        }
        return progressWrite;
      };

      this.activeAdapter = createTourAdapter({
        definition,
        router: this.$router,
        labels: this.labels(),
        waitForDom: async () => {
          await nextTick();
          await new Promise((resolve) => window.requestAnimationFrame(resolve));
        },
        onStepChange: (step) => {
          this.lastActiveStep = step;
          this.queueProgressWrite({
            definition,
            action: "progress",
            step,
            launchMode,
          });
        },
        onComplete: (step) => finish({ action: "complete", step }),
        onSkip: (step) => finish({ action: "skip", step }),
        onResumeLater: (step) => finish({
          action: "resume_later",
          step: this.lastActiveStep || step,
        }),
        onDestroyed: () => {
          this.activeTour = null;
          this.activeAdapter = null;
          this.lastActiveStep = null;
          this.restoreLauncherFocus();
        },
      });

      try {
        await this.activeAdapter.start(startIndex);
      } catch (error) {
        console.error("[product-tours] start failed", error);
        this.activeAdapter?.stop?.();
      }
    },
  },
};
</script>
